import { SerializedCartItem } from '@/hooks/usePlayGeniusCheckout/utils/cartHelpers';
import { ShopifyProduct } from '@/lib/shopify';
import {
  formatPriceWithDecimals,
  pushEvent,
  pushToDataLayer,
} from '@/utils/dataLayer';
import { getPlatform } from '@/utils/helpers';
import {
  CART_EVENT_TYPES,
  CLICKED_ITEM_TYPES,
  PAGE_LOCATIONS,
  PRODUCT_ADDED_COMPONENT_TYPES,
  PRODUCT_ADDED_PAGE_TYPES,
  PRODUCT_EVENT_TYPES,
  track,
  trackItemClickedEvent,
  trackProductEvent,
} from '@/utils/rudderstack';
import { calculateCartTotal } from './helpers';

export function trackCartQuantityChange({
  item,
  oldQuantity,
  newQuantity,
}: {
  item: SerializedCartItem;
  oldQuantity: number;
  newQuantity: number;
}): void {
  const quantityDiff = newQuantity - oldQuantity;

  if (quantityDiff === 0) {
    return;
  }

  const shopifyProduct = getShopifyProduct(item);
  const variant = shopifyProduct.variants[0];
  const quantity = Math.abs(quantityDiff);

  pushEvent(quantityDiff > 0 ? 'addToCart' : 'removeFromCart', {
    qtyVal: quantity,
    shopifyProduct,
    variant,
  });

  trackProductEvent(
    quantityDiff > 0
      ? PRODUCT_EVENT_TYPES.PRODUCT_ADDED
      : PRODUCT_EVENT_TYPES.PRODUCT_REMOVED,
    {
      url: window.location.pathname,
      productId: shopifyProduct.id,
      sku: variant.sku,
      category: shopifyProduct.productType,
      name: shopifyProduct.title,
      price: formatPriceWithDecimals(variant.price?.amount),
      quantity,
      component: PRODUCT_ADDED_COMPONENT_TYPES.QUANTITY_SELECTOR,
      pageType: PRODUCT_ADDED_PAGE_TYPES.CART,
    },
  );
}

export function trackAddToCart({
  item,
  quantity,
  selectedVariantIndex,
  component,
}: {
  item: SerializedCartItem;
  quantity: number;
  selectedVariantIndex: number;
  component: PRODUCT_ADDED_COMPONENT_TYPES;
}): void {
  const shopifyProduct = getShopifyProduct(item);
  const variant = shopifyProduct.variants[selectedVariantIndex];

  pushEvent('addToCart', {
    qtyVal: quantity,
    shopifyProduct,
    variant: shopifyProduct.variants[selectedVariantIndex],
  });

  trackProductEvent(PRODUCT_EVENT_TYPES.PRODUCT_ADDED, {
    url: window.location.pathname,
    productId: shopifyProduct.id,
    sku: variant.sku,
    category: shopifyProduct.productType,
    name: shopifyProduct.title,
    price: formatPriceWithDecimals(variant.price?.amount),
    quantity,
    component,
    pageType: PRODUCT_ADDED_PAGE_TYPES.CART,
  });
}

export function trackRemoveFromCart({
  item,
}: {
  item: SerializedCartItem;
}): void {
  const shopifyProduct = getShopifyProduct(item);
  const variant = shopifyProduct.variants[0];

  pushEvent('removeFromCart', {
    qtyVal: item.quantity,
    shopifyProduct,
    variant: shopifyProduct.variants[0],
  });

  trackProductEvent(PRODUCT_EVENT_TYPES.PRODUCT_REMOVED, {
    url: window.location.pathname,
    productId: shopifyProduct.id,
    sku: variant.sku,
    category: shopifyProduct.productType,
    name: shopifyProduct.title,
    price: formatPriceWithDecimals(variant.price?.amount),
    quantity: item.quantity,
    component: PRODUCT_ADDED_COMPONENT_TYPES.PRODUCT_CARD,
    pageType: PRODUCT_ADDED_PAGE_TYPES.CART,
  });
}

export function trackItemClicked({
  url,
  cta,
}: {
  url: string;
  cta: string;
}): void {
  trackItemClickedEvent({
    url,
    type: CLICKED_ITEM_TYPES.BUTTON,
    pageLocation: PAGE_LOCATIONS.CART,
    cta,
  });
}

export function trackContinueToCheckout({
  items,
}: {
  items: SerializedCartItem[];
}): void {
  const payload = {
    // This matches the existing event name used by the legacy cart in Lovevery.
    // TODO: Ideally we rename this to Continue to Checkout
    event: 'continueToCheckout',
    cartTotal: calculateCartTotal(items),
    ecommerce: {
      currencyCode: items[0].currency,
      checkout: {
        actionField: { step: 1 },
        products: items.map(formatProductsForAnalytics),
      },
    },
  };

  const { event, ...rest } = payload;

  pushToDataLayer(payload);
  track(event, rest);
}

export function trackCartViewed({
  title,
  items,
}: {
  title: string;
  items: SerializedCartItem[];
}) {
  const platform = getPlatform();
  const cartEntryUrl = document.referrer;
  const subtotal = calculateCartTotal(items);

  // Loop through all parent items and addons creating a product for each item
  const allProducts = items.flatMap((item) => {
    const isSubscription =
      item.attributes.find(
        (attribute) => attribute.key === 'is_playgenius_subscription',
      )?.value === 'true';

    const parentProduct = {
      ageRange: item.ageRange,
      category: item.productType,
      is_subscription: isSubscription,
      name: item.title,
      price: item.price,
      product_id: item.variantId,
      quantity: item.quantity,
      sku: item.sku,
    };

    const addonProducts = (item.addons || []).map((addon) => ({
      category: addon.productType,
      is_addon: true,
      name: addon.title,
      price: addon.price,
      product_id: addon.variantId,
      quantity: addon.quantity,
      sku: addon.sku,
    }));

    return [parentProduct, ...addonProducts];
  });

  const payload = {
    platform,
    screenWidth: window.innerWidth,
    title,
    cart_entry_url: cartEntryUrl,
    cart_entry_path: cartEntryUrl ? new URL(cartEntryUrl).pathname : '',
    products: allProducts,
    subtotal: subtotal,
    totalItemCount: allProducts.reduce((acc, item) => acc + item.quantity, 0),
    // Use a set to get the unique skus
    uniqueSkuCount: new Set(allProducts.map((p) => p.sku)).size,
    utmParams: window.location.search,
  };

  track(CART_EVENT_TYPES.CART_VIEWED, payload);
}

function getShopifyProduct(cartItem: SerializedCartItem): ShopifyProduct {
  return {
    id: cartItem.variantId,
    title: cartItem.title,
    handle: cartItem.handle,
    productType: cartItem.productType,
    tags: cartItem.tags,
    variants: cartItem.variants,
  };
}

function formatProductsForAnalytics(item: SerializedCartItem) {
  return {
    name: item.title,
    id: item.sku,
    productId: item.id?.toString(),
    variantId: item.variantId?.toString(),
    price: item.price,
    brand: 'Lovevery',
    variant: item.variantTitle,
    quantity: item.quantity,
    category: item.productType,
  };
}
